import React, { useCallback, useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import NavbarTrungThu from "../Layout/NavbarTrungThu";
import {
  createKpiPhase,
  createKpiSeason,
  deleteKpiPhase,
  deleteKpiSeason,
  fetchKpiBranches,
  fetchKpiPhases,
  fetchKpiSeasons,
  fetchPhaseDailyKpis,
  saveKpiPhaseTarget,
  saveKpiSeasonTarget,
  savePhaseDailyKpis,
  updateKpiSeason,
} from "./kpiPhaseService";
import {
  buildDailyKpiPayload,
  buildPhaseDailyRows,
  calculateDistributedKpis,
  calculateKpiFromPercentage,
  distributePercentEqually,
  formatKpiDate,
  normalizePhaseList,
  normalizeSeasonList,
  validatePhaseDailyRows,
} from "./kpiPhaseSetupUtils";
import "./updateKpiPerDay.css";

const EMPTY_SEASON_FORM = {
  name: "",
};

const EMPTY_PHASE_FORM = {
  startDate: "",
  endDate: "",
  totalKpi: "",
};

const VIEW_SEASON = "season";
const VIEW_PHASE = "phase";
const VIEW_DAILY = "daily";

const formatQuantity = (value) =>
  Number(value || 0).toLocaleString("vi-VN", { maximumFractionDigits: 0 });

const Modal = ({ title, children, onClose }) => (
  <div className="kpi-phase-modal" role="presentation" onMouseDown={onClose}>
    <section
      className="kpi-phase-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <header>
        <h2>{title}</h2>
        <button type="button" onClick={onClose} aria-label="Đóng">
          ×
        </button>
      </header>
      {children}
    </section>
  </div>
);

const UpdateKpiPerDay = () => {
  const [branches, setBranches] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [phases, setPhases] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedSeasonId, setSelectedSeasonId] = useState(null);
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [activeView, setActiveView] = useState(VIEW_SEASON);
  const [allocationMode, setAllocationMode] = useState("quantity");
  const [phaseTotalKpi, setPhaseTotalKpi] = useState("");
  const [seasonForm, setSeasonForm] = useState(EMPTY_SEASON_FORM);
  const [phaseForm, setPhaseForm] = useState(EMPTY_PHASE_FORM);
  const [modal, setModal] = useState("");
  const [notice, setNotice] = useState(null);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [loadingSeasons, setLoadingSeasons] = useState(true);
  const [loadingPhases, setLoadingPhases] = useState(false);
  const [loadingDays, setLoadingDays] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectedSeason = seasons.find(
    (season) => String(season.id) === String(selectedSeasonId)
  );
  const selectedPhase = phases.find(
    (phase) => String(phase.id) === String(selectedPhaseId)
  );
  const viewContent = {
    [VIEW_SEASON]: {
      title: "Quản lý mùa vụ",
      description: "Chọn mùa vụ cần thiết lập hoặc tạo mùa vụ mới.",
    },
    [VIEW_PHASE]: {
      title: selectedSeason ? `Phase · ${selectedSeason.name}` : "Quản lý phase",
      description: "Chọn khoảng thời gian cần thiết lập KPI.",
    },
    [VIEW_DAILY]: {
      title: selectedPhase ? `KPI ngày · ${selectedPhase.name}` : "KPI từng ngày",
      description: selectedPhase
        ? `${formatKpiDate(selectedPhase.startDate)} – ${formatKpiDate(selectedPhase.endDate)}`
        : "Phân bổ KPI cho từng ngày của phase.",
    },
  }[activeView];

  const loadSeasons = useCallback(async () => {
    if (selectedBranchId === null) {
      setSeasons([]);
      setLoadingSeasons(false);
      return;
    }

    setLoadingSeasons(true);
    try {
      const result = await fetchKpiSeasons(selectedBranchId);
      setSeasons(normalizeSeasonList(result));
    } catch (error) {
      console.error("Error fetching KPI seasons:", error);
      setSeasons([]);
      setNotice({
        type: "error",
        text: "Không thể tải danh sách mùa vụ.",
      });
    } finally {
      setLoadingSeasons(false);
    }
  }, [selectedBranchId]);

  useEffect(() => {
    const controller = new AbortController();
    const loadBranches = async () => {
      setLoadingBranches(true);
      try {
        const result = await fetchKpiBranches({ signal: controller.signal });
        const rows = Array.isArray(result?.data) ? result.data : [];
        setBranches(rows);
        setSelectedBranchId((current) => current ?? rows[0]?.ChiNhanh ?? null);
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error fetching KPI branches:", error);
        setBranches([]);
        setNotice({ type: "error", text: "Không thể tải danh sách chi nhánh." });
      } finally {
        if (!controller.signal.aborted) setLoadingBranches(false);
      }
    };

    loadBranches();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  useEffect(() => {
    if (selectedSeasonId === null) {
      setPhases([]);
      return undefined;
    }

    const controller = new AbortController();
    const loadPhases = async () => {
      setLoadingPhases(true);
      try {
        const result = await fetchKpiPhases(selectedSeasonId, selectedBranchId, {
          signal: controller.signal,
        });
        setPhases(normalizePhaseList(result));
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error fetching KPI phases:", error);
        setPhases([]);
        setNotice({ type: "error", text: "Không thể tải phase của mùa vụ." });
      } finally {
        if (!controller.signal.aborted) setLoadingPhases(false);
      }
    };

    loadPhases();
    return () => controller.abort();
  }, [selectedBranchId, selectedSeasonId]);

  useEffect(() => {
    if (!selectedPhase) {
      setDailyRows([]);
      return undefined;
    }

    const controller = new AbortController();
    const loadDailyKpis = async () => {
      setLoadingDays(true);
      try {
        const result = await fetchPhaseDailyKpis(
          selectedPhase.id,
          selectedBranchId,
          { signal: controller.signal }
        );
        const totalKpi = selectedPhase.totalKpi;

        setPhaseTotalKpi(totalKpi === null ? "" : String(totalKpi));
        setAllocationMode("quantity");
        setDailyRows(buildPhaseDailyRows(selectedPhase, result));
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Error fetching phase daily KPI:", error);
        setDailyRows(buildPhaseDailyRows(selectedPhase, []));
        setPhaseTotalKpi(
          selectedPhase.totalKpi === null ? "" : String(selectedPhase.totalKpi)
        );
        setAllocationMode("quantity");
        setNotice({ type: "error", text: "Không thể tải KPI ngày của phase." });
      } finally {
        if (!controller.signal.aborted) setLoadingDays(false);
      }
    };

    loadDailyKpis();
    return () => controller.abort();
  }, [selectedBranchId, selectedPhase]);

  const totalPercentage = useMemo(
    () =>
      dailyRows.reduce((sum, row) => sum + (Number(row.percentage) || 0), 0),
    [dailyRows]
  );
  const distributedKpis = useMemo(
    () => calculateDistributedKpis(phaseTotalKpi, dailyRows),
    [dailyRows, phaseTotalKpi]
  );
  const totalDailyKpi = useMemo(
    () =>
      dailyRows.reduce(
        (sum, row, index) =>
          sum +
          (allocationMode === "percentage"
            ? distributedKpis[index] || 0
            : Number(row.originalKpi) || 0),
        0
      ),
    [allocationMode, dailyRows, distributedKpis]
  );

  const selectBranch = (branchId) => {
    setSelectedBranchId(branchId);
    setSeasons([]);
    setSelectedSeasonId(null);
    setSelectedPhaseId(null);
    setPhases([]);
    setDailyRows([]);
    setActiveView(VIEW_SEASON);
    setLoadingSeasons(true);
    setNotice(null);
  };

  const selectSeason = (season) => {
    setSelectedSeasonId(season.id);
    setSelectedPhaseId(null);
    setDailyRows([]);
    setActiveView(VIEW_PHASE);
    setNotice(null);
  };

  const selectPhase = (phase) => {
    setSelectedPhaseId(phase.id);
    setActiveView(VIEW_DAILY);
    setNotice(null);
  };

  const openSeasonView = () => {
    setActiveView(VIEW_SEASON);
    setNotice(null);
  };

  const openPhaseView = () => {
    if (!selectedSeason) return;
    setActiveView(VIEW_PHASE);
    setNotice(null);
  };

  const openDailyView = () => {
    if (!selectedPhase) return;
    setActiveView(VIEW_DAILY);
    setNotice(null);
  };

  const openSeasonModal = () => {
    setSeasonForm(EMPTY_SEASON_FORM);
    setModal("season");
    setNotice(null);
  };

  const openSeasonEditModal = () => {
    if (!selectedSeason) return;
    setSeasonForm({ name: selectedSeason.name });
    setModal("season-edit");
    setNotice(null);
  };

  const openPhaseModal = () => {
    setPhaseForm(EMPTY_PHASE_FORM);
    setModal("phase");
    setNotice(null);
  };

  const handleCreateSeason = async (event) => {
    event.preventDefault();
    if (!seasonForm.name.trim()) {
      setNotice({ type: "error", text: "Tên mùa vụ không được để trống." });
      return;
    }

    setSaving(true);
    try {
      const result = await createKpiSeason({
        TenMuaVu: seasonForm.name.trim(),
      });
      const seasonId = result?.data?.IDMua;
      if (!seasonId) throw new Error("API không trả IDMua của mùa vụ mới.");
      await saveKpiSeasonTarget(seasonId, selectedBranchId, {
        Target: 0,
        NgayApDung: null,
        NgayKetThuc: null,
        BD: 0,
        BNH: 0,
        BNG: 0,
        BanhBan: 0,
        BanhDat: 0,
      });
      setModal("");
      await loadSeasons();
      setSelectedSeasonId(seasonId);
      setActiveView(VIEW_PHASE);
      setNotice({ type: "success", text: "Đã thêm mùa vụ mới." });
    } catch (error) {
      console.error("Error creating KPI season:", error);
      setNotice({ type: "error", text: error.message || "Không thể thêm mùa vụ." });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSeason = async (event) => {
    event.preventDefault();
    if (!selectedSeason || !seasonForm.name.trim()) {
      setNotice({ type: "error", text: "Tên mùa vụ không được để trống." });
      return;
    }

    setSaving(true);
    try {
      await updateKpiSeason(selectedSeason.id, {
        TenMuaVu: seasonForm.name.trim(),
      });
      setModal("");
      await loadSeasons();
      setNotice({ type: "success", text: "Đã cập nhật tên mùa vụ." });
    } catch (error) {
      console.error("Error updating KPI season:", error);
      setNotice({
        type: "error",
        text: error.message || "Không thể cập nhật mùa vụ.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeason = async () => {
    if (!selectedSeason) return;

    setSaving(true);
    try {
      await deleteKpiSeason(selectedSeason.id);
      setSelectedSeasonId(null);
      setSelectedPhaseId(null);
      setPhases([]);
      setDailyRows([]);
      setActiveView(VIEW_SEASON);
      setModal("");
      await loadSeasons();
      setNotice({ type: "success", text: "Đã xóa mùa vụ." });
    } catch (error) {
      console.error("Error deleting KPI season:", error);
      setNotice({
        type: "error",
        text:
          error.message ||
          "Không thể xóa mùa vụ. Hãy xóa hết phase và KPI chi nhánh trước.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePhase = async (event) => {
    event.preventDefault();
    if (
      !selectedSeason ||
      !phaseForm.startDate ||
      !phaseForm.endDate ||
      phaseForm.startDate > phaseForm.endDate
    ) {
      setNotice({
        type: "error",
        text: "Khoảng ngày phase phải đúng thứ tự.",
      });
      return;
    }

    const overlaps = phases.some(
      (phase) =>
        phaseForm.startDate <= phase.endDate && phaseForm.endDate >= phase.startDate
    );
    if (overlaps) {
      setNotice({ type: "error", text: "Khoảng ngày phase đang trùng phase khác." });
      return;
    }

    setSaving(true);
    try {
      const createResult = await createKpiPhase({
        IDMuaVu: selectedSeason.id,
        Phase: phases.reduce((max, phase) => Math.max(max, phase.number), 0) + 1,
        NgayBatDau: phaseForm.startDate,
        NgayKetThuc: phaseForm.endDate,
        ChiNhanh: Number(selectedBranchId),
        KPI: Number(phaseForm.totalKpi) > 0 ? Number(phaseForm.totalKpi) : null,
      });
      const phaseId = createResult?.data?.ID;
      if (!phaseId) throw new Error("API không trả ID của phase mới.");
      await saveKpiPhaseTarget(phaseId, selectedBranchId, {
        KPI: Number(phaseForm.totalKpi) > 0 ? Number(phaseForm.totalKpi) : 0,
        NgayBatDau: phaseForm.startDate,
        NgayKetThuc: phaseForm.endDate,
      });
      setModal("");
      const result = await fetchKpiPhases(
        selectedSeason.id,
        selectedBranchId
      );
      setPhases(normalizePhaseList(result));
      setSelectedPhaseId(phaseId);
      setActiveView(VIEW_DAILY);
      await loadSeasons();
      setNotice({ type: "success", text: "Đã thêm phase mới." });
    } catch (error) {
      console.error("Error creating KPI phase:", error);
      setNotice({ type: "error", text: error.message || "Không thể thêm phase." });
    } finally {
      setSaving(false);
    }
  };

  const openPhaseTargetModal = () => {
    setPhaseForm({
      startDate: selectedPhase.startDate,
      endDate: selectedPhase.endDate,
      totalKpi:
        selectedPhase.totalKpi === null ? "" : String(selectedPhase.totalKpi),
    });
    setNotice(null);
    setModal("phase-target");
  };

  const handleUpdatePhaseTarget = async (event) => {
    event.preventDefault();
    if (
      !selectedPhase ||
      !phaseForm.startDate ||
      !phaseForm.endDate ||
      phaseForm.startDate > phaseForm.endDate
    ) {
      setNotice({
        type: "error",
        text: "Khoảng ngày phase phải đúng thứ tự.",
      });
      return;
    }

    const overlaps = phases.some(
      (phase) =>
        String(phase.id) !== String(selectedPhase.id) &&
        phaseForm.startDate <= phase.endDate &&
        phaseForm.endDate >= phase.startDate
    );
    if (overlaps) {
      setNotice({ type: "error", text: "Khoảng ngày phase đang trùng phase khác." });
      return;
    }

    setSaving(true);
    try {
      await saveKpiPhaseTarget(selectedPhase.id, selectedBranchId, {
        KPI: Number(phaseForm.totalKpi) > 0 ? Number(phaseForm.totalKpi) : 0,
        NgayBatDau: phaseForm.startDate,
        NgayKetThuc: phaseForm.endDate,
      });
      const result = await fetchKpiPhases(
        selectedSeason.id,
        selectedBranchId
      );
      setPhases(normalizePhaseList(result));
      setModal("");
      setNotice({
        type: "success",
        text: `Đã cập nhật KPI và thời gian ${selectedPhase.name} cho chi nhánh đang chọn.`,
      });
    } catch (error) {
      console.error("Error saving branch phase target:", error);
      setNotice({
        type: "error",
        text: error.message || "Không thể cập nhật KPI và thời gian phase.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePhase = async () => {
    if (!selectedPhase || !selectedSeason) return;

    setSaving(true);
    try {
      await deleteKpiPhase(selectedPhase.id);
      const result = await fetchKpiPhases(
        selectedSeason.id,
        selectedBranchId
      );
      setPhases(normalizePhaseList(result));
      setSelectedPhaseId(null);
      setDailyRows([]);
      setActiveView(VIEW_PHASE);
      setModal("");
      await loadSeasons();
      setNotice({ type: "success", text: "Đã xóa phase." });
    } catch (error) {
      console.error("Error deleting KPI phase:", error);
      setNotice({
        type: "error",
        text:
          error.message ||
          "Không thể xóa phase. Phase đã có dữ liệu KPI từng ngày.",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateDailyRow = (date, field, value) => {
    setDailyRows((current) =>
      current.map((row) => (row.date === date ? { ...row, [field]: value } : row))
    );
    setNotice(null);
  };

  const changeAllocationMode = (mode) => {
    if (mode === "percentage" && !(Number(phaseTotalKpi) > 0)) return;
    setDailyRows((current) =>
      current.map((row) => {
        if (mode === "quantity") {
          return {
            ...row,
            originalKpi:
              row.originalKpi ||
              String(calculateKpiFromPercentage(phaseTotalKpi, row.percentage)),
          };
        }
        return {
          ...row,
          percentage:
            row.percentage ||
            (Number(row.originalKpi) > 0
              ? String(((Number(row.originalKpi) / Number(phaseTotalKpi)) * 100).toFixed(2))
              : ""),
        };
      })
    );
    setAllocationMode(mode);
    setNotice(null);
  };

  const handleSaveDailyKpis = async () => {
    const validationError = validatePhaseDailyRows({
      totalKpi: phaseTotalKpi,
      mode: allocationMode,
      rows: dailyRows,
    });
    if (validationError) {
      setNotice({ type: "error", text: validationError });
      return;
    }

    setSaving(true);
    try {
      const targetKpi =
        allocationMode === "percentage"
          ? Number(phaseTotalKpi)
          : totalDailyKpi;
      await savePhaseDailyKpis(
        selectedPhase.id,
        selectedBranchId,
        buildDailyKpiPayload({
          totalKpi: phaseTotalKpi,
          mode: allocationMode,
          rows: dailyRows,
        })
      );
      await saveKpiPhaseTarget(selectedPhase.id, selectedBranchId, {
        KPI: targetKpi,
        NgayBatDau: selectedPhase.startDate,
        NgayKetThuc: selectedPhase.endDate,
      });
      setPhaseTotalKpi(String(targetKpi));
      const phaseResult = await fetchKpiPhases(
        selectedSeason.id,
        selectedBranchId
      );
      setPhases(normalizePhaseList(phaseResult));
      setNotice({
        type: "success",
        text: `Đã lưu KPI ${dailyRows.length} ngày của ${selectedPhase.name}.`,
      });
    } catch (error) {
      console.error("Error saving phase daily KPI:", error);
      setNotice({ type: "error", text: error.message || "Không thể lưu KPI phase." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <NavbarTrungThu />
      <main className="kpi-phase-page">
        <header className="kpi-phase-hero">
          <div>
            <span>Quản lý kế hoạch bán hàng</span>
            <h1>{viewContent.title}</h1>
            <p>{viewContent.description}</p>
          </div>
          <div className="kpi-phase-hero__aside">
            <label className="kpi-phase-branch-filter">
              <span>Chi nhánh áp dụng</span>
              <select
                value={selectedBranchId ?? ""}
                disabled={loadingBranches || branches.length === 0}
                onChange={(event) => selectBranch(Number(event.target.value))}
              >
                {selectedBranchId === null && (
                  <option value="" disabled>
                    {loadingBranches ? "Đang tải chi nhánh..." : "Chọn chi nhánh"}
                  </option>
                )}
                {branches.map((branch) => (
                  <option key={branch.ChiNhanh} value={branch.ChiNhanh}>
                    {branch.TenChiNhanh}
                  </option>
                ))}
              </select>
            </label>
            <div className="kpi-phase-hero__steps" aria-label="Quy trình thiết lập">
              <b className={activeView === VIEW_SEASON ? "is-current" : "is-done"}>1</b>
              <i />
              <b
                className={
                  activeView === VIEW_PHASE
                    ? "is-current"
                    : activeView === VIEW_DAILY
                      ? "is-done"
                      : ""
                }
              >
                2
              </b>
              <i />
              <b className={activeView === VIEW_DAILY ? "is-current" : ""}>3</b>
            </div>
          </div>
        </header>

        <nav className="kpi-phase-view-nav" aria-label="Điều hướng thiết lập KPI">
          <button
            type="button"
            className={activeView === VIEW_SEASON ? "is-active" : ""}
            onClick={openSeasonView}
          >
            <span>01</span>
            <span>
              <strong>Mùa vụ</strong>
              <small>Danh sách mùa vụ</small>
            </span>
          </button>
          <button
            type="button"
            className={activeView === VIEW_PHASE ? "is-active" : ""}
            disabled={!selectedSeason}
            onClick={openPhaseView}
          >
            <span>02</span>
            <span>
              <strong>Phase</strong>
              <small>{selectedSeason?.name || "Chọn mùa vụ trước"}</small>
            </span>
          </button>
          <button
            type="button"
            className={activeView === VIEW_DAILY ? "is-active" : ""}
            disabled={!selectedPhase}
            onClick={openDailyView}
          >
            <span>03</span>
            <span>
              <strong>KPI ngày</strong>
              <small>{selectedPhase?.name || "Chọn phase trước"}</small>
            </span>
          </button>
        </nav>

        {activeView === VIEW_SEASON && (
          <section className="kpi-phase-section kpi-phase-view-panel">
          <header className="kpi-phase-section__header">
            <div>
              <span className="kpi-phase-step">01</span>
              <div>
                <h2>Mùa vụ</h2>
                <p>Chọn mùa vụ cần thiết lập hoặc tạo mùa vụ mới.</p>
              </div>
            </div>
            <div className="kpi-phase-header-actions">
              {selectedSeason && (
                <>
                  <button
                    className="kpi-phase-secondary-button"
                    type="button"
                    onClick={openSeasonEditModal}
                  >
                    Sửa mùa vụ
                  </button>
                  <button
                    className="kpi-phase-danger-button"
                    type="button"
                    onClick={() => {
                      setNotice(null);
                      setModal("season-delete");
                    }}
                  >
                    Xóa mùa vụ
                  </button>
                </>
              )}
              <button
                className="kpi-phase-primary-button"
                type="button"
                disabled={selectedBranchId === null}
                onClick={openSeasonModal}
              >
                + Thêm mùa vụ
              </button>
            </div>
          </header>

          {loadingBranches || loadingSeasons ? (
            <div className="kpi-phase-loading">Đang tải mùa vụ...</div>
          ) : branches.length === 0 ? (
            <div className="kpi-phase-empty">
              <b>Không có chi nhánh</b>
              <span>API chưa trả danh sách chi nhánh khả dụng.</span>
            </div>
          ) : seasons.length > 0 ? (
            <div className="kpi-season-grid">
              {seasons.map((season) => (
                <button
                  type="button"
                  key={season.id}
                  className={
                    String(selectedSeasonId) === String(season.id) ? "is-active" : ""
                  }
                  onClick={() => selectSeason(season)}
                >
                  <span>
                    {season.year || "Mùa vụ"} · ID {season.id}
                  </span>
                  <strong>{season.name}</strong>
                  <small>
                    {season.startDate && season.endDate
                      ? `${formatKpiDate(season.startDate)} – ${formatKpiDate(season.endDate)}`
                      : "Chưa có khoảng ngày"}
                  </small>
                  <em>{season.phaseCount} phase</em>
                </button>
              ))}
            </div>
          ) : (
            <div className="kpi-phase-empty">
              <b>Chưa có mùa vụ</b>
              <span>Nhấn “Thêm mùa vụ” để bắt đầu thiết lập.</span>
            </div>
          )}
          </section>
        )}

        {activeView === VIEW_PHASE && selectedSeason && (
          <section className="kpi-phase-section kpi-phase-view-panel">
            <header className="kpi-phase-section__header">
              <div>
                <span className="kpi-phase-step">02</span>
                <div>
                  <h2>Phase của {selectedSeason.name}</h2>
                  <p>Mỗi phase là một khoảng thời gian và có thể có KPI tổng.</p>
                </div>
              </div>
              <div className="kpi-phase-header-actions">
                <button
                  className="kpi-phase-secondary-button"
                  type="button"
                  onClick={openSeasonView}
                >
                  ← Mùa vụ
                </button>
                <button className="kpi-phase-primary-button" type="button" onClick={openPhaseModal}>
                  + Thêm phase
                </button>
              </div>
            </header>

            {loadingPhases ? (
              <div className="kpi-phase-loading">Đang tải phase...</div>
            ) : phases.length > 0 ? (
              <div className="kpi-phase-grid">
                {phases.map((phase) => (
                  <button
                    type="button"
                    key={phase.id}
                    className={
                      String(selectedPhaseId) === String(phase.id) ? "is-active" : ""
                    }
                    onClick={() => selectPhase(phase)}
                  >
                    <span>Phase {String(phase.number).padStart(2, "0")}</span>
                    <strong>{phase.name}</strong>
                    <small>
                      {formatKpiDate(phase.startDate)} – {formatKpiDate(phase.endDate)}
                    </small>
                    <div>
                      <em>KPI phase</em>
                      <b>{phase.totalKpi === null ? "Chưa thiết lập" : formatQuantity(phase.totalKpi)}</b>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="kpi-phase-empty">
                <b>Mùa vụ chưa có phase</b>
                <span>Nhấn “Thêm phase” để tạo khoảng KPI đầu tiên.</span>
              </div>
            )}
          </section>
        )}

        {activeView === VIEW_DAILY && selectedPhase && (
          <section className="kpi-phase-section kpi-phase-detail kpi-phase-view-panel">
            <header className="kpi-phase-section__header">
              <div>
                <span className="kpi-phase-step">03</span>
                <div>
                  <h2>KPI từng ngày · {selectedPhase.name}</h2>
                  <p>
                    {formatKpiDate(selectedPhase.startDate)} – {formatKpiDate(selectedPhase.endDate)}
                  </p>
                </div>
              </div>
              <div className="kpi-phase-header-actions">
                <button
                  type="button"
                  className="kpi-phase-secondary-button"
                  onClick={openPhaseView}
                >
                  ← Phase
                </button>
                <button
                  type="button"
                  className="kpi-phase-secondary-button"
                  onClick={openPhaseTargetModal}
                >
                  Chỉnh KPI &amp; thời gian
                </button>
                <button
                  type="button"
                  className="kpi-phase-danger-button"
                  onClick={() => {
                    setNotice(null);
                    setModal("phase-delete");
                  }}
                >
                  Xóa phase
                </button>
              </div>
            </header>

            <div className="kpi-phase-allocation">
              <label>
                <span>KPI tổng của phase (không bắt buộc)</span>
                <input
                  type="number"
                  min="0"
                  value={phaseTotalKpi}
                  placeholder="Để trống nếu nhập số lượng từng ngày"
                  onChange={(event) => {
                    setPhaseTotalKpi(event.target.value);
                    if (!(Number(event.target.value) > 0)) setAllocationMode("quantity");
                    setNotice(null);
                  }}
                />
              </label>
              <div className="kpi-phase-mode">
                <span>Cách nhập KPI ngày</span>
                <div>
                  <button
                    type="button"
                    className={allocationMode === "percentage" ? "is-active" : ""}
                    disabled={!(Number(phaseTotalKpi) > 0)}
                    onClick={() => changeAllocationMode("percentage")}
                  >
                    Phân bổ theo %
                  </button>
                  <button
                    type="button"
                    className={allocationMode === "quantity" ? "is-active" : ""}
                    onClick={() => changeAllocationMode("quantity")}
                  >
                    Nhập số lượng
                  </button>
                </div>
              </div>
              {allocationMode === "percentage" && (
                <button
                  type="button"
                  className="kpi-phase-equal-button"
                  onClick={() => setDailyRows((current) => distributePercentEqually(current))}
                >
                  Chia đều 100% cho các ngày
                </button>
              )}
            </div>

            <div className="kpi-phase-summary">
              <div>
                <span>Số ngày</span>
                <strong>{dailyRows.length}</strong>
              </div>
              <div>
                <span>{allocationMode === "percentage" ? "Tổng tỷ lệ" : "KPI đã nhập"}</span>
                <strong>
                  {allocationMode === "percentage"
                    ? `${totalPercentage.toFixed(2)}%`
                    : formatQuantity(totalDailyKpi)}
                </strong>
              </div>
              <div>
                <span>KPI gốc sau phân bổ</span>
                <strong>{formatQuantity(totalDailyKpi)}</strong>
              </div>
            </div>

            {loadingDays ? (
              <div className="kpi-phase-loading">Đang tải KPI từng ngày...</div>
            ) : (
              <div className="kpi-phase-days">
                <div className="kpi-phase-days__header">
                  <span>Ngày</span>
                  <span>{allocationMode === "percentage" ? "Tỷ lệ phân bổ" : "KPI gốc"}</span>
                  <span>KPI gốc</span>
                </div>
                {dailyRows.map((row, index) => {
                  const calculatedKpi =
                    allocationMode === "percentage"
                      ? distributedKpis[index] || 0
                      : Number(row.originalKpi) || 0;
                  return (
                    <div className="kpi-phase-day-row" key={row.date}>
                      <strong>{formatKpiDate(row.date)}</strong>
                      <label>
                        <span className="kpi-phase-mobile-label">
                          {allocationMode === "percentage" ? "Tỷ lệ" : "KPI gốc"}
                        </span>
                        <div className="kpi-phase-number-input">
                          <input
                            type="number"
                            min="0"
                            step={allocationMode === "percentage" ? "0.01" : "1"}
                            value={
                              allocationMode === "percentage"
                                ? row.percentage
                                : row.originalKpi
                            }
                            placeholder="0"
                            onChange={(event) =>
                              updateDailyRow(
                                row.date,
                                allocationMode === "percentage"
                                  ? "percentage"
                                  : "originalKpi",
                                event.target.value
                              )
                            }
                          />
                          {allocationMode === "percentage" && <span>%</span>}
                        </div>
                      </label>
                      <b>{formatQuantity(calculatedKpi)}</b>
                    </div>
                  );
                })}
              </div>
            )}

            <footer className="kpi-phase-actions">
              <div>
                {allocationMode === "percentage"
                  ? "Tổng phần trăm phải bằng 100%."
                  : "Khi không có KPI tổng phase, phải nhập số lượng cho từng ngày."}
              </div>
              <button
                type="button"
                disabled={saving || loadingDays || dailyRows.length === 0}
                onClick={handleSaveDailyKpis}
              >
                {saving ? "Đang lưu..." : "Lưu KPI phase"}
              </button>
            </footer>
          </section>
        )}

        {notice && (
          <div className={`kpi-phase-notice is-${notice.type}`} role="alert">
            {notice.text}
          </div>
        )}
      </main>

      {modal === "season" && (
        <Modal title="Thêm mùa vụ" onClose={() => setModal("")}>
          <form className="kpi-phase-form" onSubmit={handleCreateSeason}>
            {notice && (
              <div className={`kpi-phase-form__notice is-${notice.type}`}>
                {notice.text}
              </div>
            )}
            <label>
              <span>Tên mùa vụ</span>
              <input
                value={seasonForm.name}
                placeholder="Ví dụ: Mùa vụ 2026"
                onChange={(event) =>
                  setSeasonForm((current) => ({ ...current, name: event.target.value }))
                }
              />
            </label>
            <p className="kpi-phase-form__hint">
              Mùa vụ được tạo dùng chung; KPI và khoảng ngày được quản lý theo từng chi nhánh.
            </p>
            <footer>
              <button type="button" onClick={() => setModal("")}>Hủy</button>
              <button type="submit" disabled={saving}>{saving ? "Đang thêm..." : "Thêm mùa vụ"}</button>
            </footer>
          </form>
        </Modal>
      )}

      {modal === "season-edit" && selectedSeason && (
        <Modal title={`Sửa mùa vụ · ${selectedSeason.name}`} onClose={() => setModal("")}>
          <form className="kpi-phase-form" onSubmit={handleUpdateSeason}>
            {notice && (
              <div className={`kpi-phase-form__notice is-${notice.type}`}>
                {notice.text}
              </div>
            )}
            <label>
              <span>Tên mùa vụ</span>
              <input
                value={seasonForm.name}
                onChange={(event) =>
                  setSeasonForm((current) => ({ ...current, name: event.target.value }))
                }
              />
            </label>
            <footer>
              <button type="button" onClick={() => setModal("")}>Hủy</button>
              <button type="submit" disabled={saving}>
                {saving ? "Đang lưu..." : "Lưu thay đổi"}
              </button>
            </footer>
          </form>
        </Modal>
      )}

      {modal === "season-delete" && selectedSeason && (
        <Modal title="Xác nhận xóa mùa vụ" onClose={() => setModal("")}>
          <div className="kpi-phase-form">
            {notice && (
              <div className={`kpi-phase-form__notice is-${notice.type}`}>
                {notice.text}
              </div>
            )}
            <p className="kpi-phase-form__hint">
              Xóa mùa vụ <strong>{selectedSeason.name}</strong>? API chỉ cho phép xóa khi mùa vụ không còn phase hoặc KPI chi nhánh.
            </p>
            <footer>
              <button type="button" onClick={() => setModal("")}>Hủy</button>
              <button
                type="button"
                className="kpi-phase-danger-button"
                disabled={saving}
                onClick={handleDeleteSeason}
              >
                {saving ? "Đang xóa..." : "Xóa mùa vụ"}
              </button>
            </footer>
          </div>
        </Modal>
      )}

      {modal === "phase" && selectedSeason && (
        <Modal title={`Thêm phase · ${selectedSeason.name}`} onClose={() => setModal("")}>
          <form className="kpi-phase-form" onSubmit={handleCreatePhase}>
            {notice && (
              <div className={`kpi-phase-form__notice is-${notice.type}`}>
                {notice.text}
              </div>
            )}
            <p className="kpi-phase-form__hint">
              Sẽ tạo Phase {phases.reduce((max, phase) => Math.max(max, phase.number), 0) + 1} cho chi nhánh đang chọn.
            </p>
            <div className="kpi-phase-form__dates">
              <label>
                <span>Từ ngày</span>
                <input
                  type="date"
                  value={phaseForm.startDate}
                  onChange={(event) =>
                    setPhaseForm((current) => ({ ...current, startDate: event.target.value }))
                  }
                />
              </label>
              <label>
                <span>Đến ngày</span>
                <input
                  type="date"
                  value={phaseForm.endDate}
                  onChange={(event) =>
                    setPhaseForm((current) => ({ ...current, endDate: event.target.value }))
                  }
                />
              </label>
            </div>
            <label>
              <span>KPI tổng phase (không bắt buộc)</span>
              <input
                type="number"
                min="0"
                value={phaseForm.totalKpi}
                placeholder="Để trống nếu sẽ nhập số lượng từng ngày"
                onChange={(event) =>
                  setPhaseForm((current) => ({ ...current, totalKpi: event.target.value }))
                }
              />
            </label>
            <p className="kpi-phase-form__hint">
              Có KPI tổng: được phân bổ % theo ngày. Không có KPI tổng: bắt buộc nhập số lượng từng ngày.
            </p>
            <footer>
              <button type="button" onClick={() => setModal("")}>Hủy</button>
              <button type="submit" disabled={saving}>{saving ? "Đang thêm..." : "Thêm phase"}</button>
            </footer>
          </form>
        </Modal>
      )}

      {modal === "phase-target" && selectedSeason && selectedPhase && (
        <Modal
          title={`Chỉnh ${selectedPhase.name} · ${selectedSeason.name}`}
          onClose={() => setModal("")}
        >
          <form className="kpi-phase-form" onSubmit={handleUpdatePhaseTarget}>
            {notice && (
              <div className={`kpi-phase-form__notice is-${notice.type}`}>
                {notice.text}
              </div>
            )}
            <p className="kpi-phase-form__hint">
              KPI và khoảng thời gian này chỉ áp dụng cho chi nhánh đang chọn.
            </p>
            <div className="kpi-phase-form__dates">
              <label>
                <span>Từ ngày</span>
                <input
                  type="date"
                  value={phaseForm.startDate}
                  onChange={(event) =>
                    setPhaseForm((current) => ({
                      ...current,
                      startDate: event.target.value,
                    }))
                  }
                />
              </label>
              <label>
                <span>Đến ngày</span>
                <input
                  type="date"
                  value={phaseForm.endDate}
                  onChange={(event) =>
                    setPhaseForm((current) => ({
                      ...current,
                      endDate: event.target.value,
                    }))
                  }
                />
              </label>
            </div>
            <label>
              <span>KPI tổng phase</span>
              <input
                type="number"
                min="0"
                value={phaseForm.totalKpi}
                placeholder="0"
                onChange={(event) =>
                  setPhaseForm((current) => ({
                    ...current,
                    totalKpi: event.target.value,
                  }))
                }
              />
            </label>
            <footer>
              <button type="button" onClick={() => setModal("")}>Hủy</button>
              <button type="submit" disabled={saving}>
                {saving ? "Đang lưu..." : "Lưu KPI & thời gian"}
              </button>
            </footer>
          </form>
        </Modal>
      )}

      {modal === "phase-delete" && selectedSeason && selectedPhase && (
        <Modal title="Xác nhận xóa phase" onClose={() => setModal("")}>
          <div className="kpi-phase-form">
            {notice && (
              <div className={`kpi-phase-form__notice is-${notice.type}`}>
                {notice.text}
              </div>
            )}
            <p className="kpi-phase-form__hint">
              Xóa <strong>{selectedPhase.name}</strong> khỏi mùa vụ <strong>{selectedSeason.name}</strong>? API chỉ cho phép xóa phase chưa có dữ liệu KPI từng ngày.
            </p>
            <footer>
              <button type="button" onClick={() => setModal("")}>Hủy</button>
              <button
                type="button"
                className="kpi-phase-danger-button"
                disabled={saving}
                onClick={handleDeletePhase}
              >
                {saving ? "Đang xóa..." : "Xóa phase"}
              </button>
            </footer>
          </div>
        </Modal>
      )}
    </>
  );
};

export default UpdateKpiPerDay;
